import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import {
  ApiError,
  createOwnedPlaybackSession,
  createPreviewSession,
  queryKeys,
  useSessionQuery,
  useSignOutMutation,
  useWorkspaceDiscovery,
} from './api/index.js';
import {
  AppProviders,
  AppRoutes,
  type SessionResource,
  type WorkspaceResource,
} from './app/index.js';
import { queryClient } from './app/query-client.js';
import { ActivityPage } from './features/activity/index.js';
import { LoginPage } from './features/auth/login-page.js';
import { CatalogPage } from './features/catalog/index.js';
import { BulkCreatePage, CreatePage } from './features/create/index.js';
import { DeliveryDetailPage } from './features/delivery/index.js';
import { GenerationPage } from './features/generations/index.js';
import { LibraryPage } from './features/library/index.js';
import { PlaylistDetailPage, PlaylistsPage } from './features/playlists/index.js';
import { ProjectDetailPage, ProjectsPage } from './features/projects/index.js';
import {
  Kk7BoundaryAuditPage,
  OnsetAuditPage,
  OpeningImpactAuditPage,
} from './features/onset-audit/index.js';
import { SettingsPage } from './features/settings/index.js';
import { TrackDetailPage } from './features/tracks/index.js';
import type { ResolvePlaybackSource } from './player/index.js';

function Application() {
  const session = useSessionQuery();
  const sessionAuthenticated = Boolean(session.data && !session.error);
  const workspace = useWorkspaceDiscovery(sessionAuthenticated);
  const signOut = useSignOutMutation();
  const client = useQueryClient();

  const sessionResource: SessionResource = session.isLoading
    ? { status: 'loading' }
    : session.error instanceof ApiError && session.error.status === 401
      ? { status: 'unauthenticated' }
      : session.error
        ? {
            status: 'unavailable',
            error: session.error,
            retrying: session.isFetching,
            retry: () => {
              if (!session.isFetching) void session.refetch({ cancelRefetch: false });
            },
          }
        : session.data
          ? { status: 'authenticated', session: session.data.user }
          : { status: 'unauthenticated' };

  const workspaceError = workspace.connections.error ?? workspace.libraries.error;
  const workspaceResource: WorkspaceResource =
    !sessionAuthenticated || workspace.connections.isLoading || workspace.libraries.isLoading
      ? { status: 'loading' }
      : workspaceError
        ? {
            status: 'error',
            error: workspaceError,
            retry: () => {
              void workspace.connections.refetch();
              void workspace.libraries.refetch();
            },
          }
        : workspace.connection && workspace.library
          ? {
              status: 'ready',
              workspace: {
                connection: workspace.connection,
                library: workspace.library,
              },
            }
          : { status: 'empty' };

  const resolvePlaybackSource: ResolvePlaybackSource = async (item) => {
    if (item.sourcePreference === 'direct') {
      if (!item.directUrl) throw new Error('The direct audit source is missing its audio URL.');
      return {
        url: item.directUrl,
        label: 'audit source',
        contentType: 'audio/ogg',
        expiresAt: null,
      };
    }
    const descriptor =
      item.sourcePreference === 'owned'
        ? item.libraryId && item.importId
          ? await createOwnedPlaybackSession(item.libraryId, item.importId)
          : await Promise.reject(
              new Error('The managed WAV source is missing its Library import identity.'),
            )
        : await createPreviewSession(item.id);
    return {
      url: descriptor.playback_url,
      label: descriptor.transport === 'owned_asset' ? 'owned WAV' : 'provider preview',
      contentType: descriptor.content_type,
      expiresAt: descriptor.expires_at,
    };
  };

  return (
    <AppProviders
      playbackPersistenceScope={
        sessionResource.status === 'authenticated' && workspaceResource.status === 'ready'
          ? `${sessionResource.session.email}:${workspaceResource.workspace.connection.id}:${workspaceResource.workspace.library.id}`
          : null
      }
      resolvePlaybackSource={resolvePlaybackSource}
    >
      <AppRoutes
        session={sessionResource}
        workspace={workspaceResource}
        onSignOut={async () => {
          await signOut.mutateAsync();
          await client.invalidateQueries({ queryKey: queryKeys.session });
        }}
        routes={{
          login: <LoginPage session={sessionResource} />,
          create: <CreatePage />,
          bulkCreate: <BulkCreatePage />,
          projects: <ProjectsPage />,
          project: <ProjectDetailPage />,
          openingImpactAudit: <OpeningImpactAuditPage />,
          kk7BoundaryAudit: <Kk7BoundaryAuditPage />,
          onsetAudit: <OnsetAuditPage />,
          generation: <GenerationPage />,
          catalog: <CatalogPage />,
          library: <LibraryPage />,
          track: <TrackDetailPage />,
          playlists: <PlaylistsPage />,
          playlist: <PlaylistDetailPage />,
          activity: <ActivityPage />,
          settings: <SettingsPage />,
          delivery: <DeliveryDetailPage />,
        }}
      />
    </AppProviders>
  );
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Application />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
